import { useState } from "react";
import { documentHistoryPath, useApi } from "../api/client";
import type { DocumentAttempt, DocumentHistory as History } from "../api/types";
import { Empty, ErrorState, Loading } from "./States";
import { EM_DASH, fmtDateTime, fmtMs, humanise } from "../lib/format";

const pct = (v: number | null) => (v === null ? EM_DASH : `${v}%`);

type Change = "gained" | "lost" | "changed" | "same";

/** What one attempt read for one field, against what the attempt before read. */
interface FieldDiff {
  field: string;
  now: string | null;
  before: string | null;
  change: Change;
}

/**
 * Compare two attempts field by field.
 *
 * `null` means the field was not in that extraction at all; `""` means it was
 * there and empty. They are different facts — one says this version of the
 * extraction had no such field, the other that the agent looked and found
 * nothing — so they are kept apart and rendered differently.
 */
function diff(
  now: Record<string, string>,
  before: Record<string, string> | null,
): FieldDiff[] {
  const names = new Set([
    ...Object.keys(now ?? {}),
    ...Object.keys(before ?? {}),
  ]);
  const filled = (v: string | null) => v !== null && v.trim() !== "";
  const out: FieldDiff[] = [];
  for (const field of [...names]) {
    const a = now?.[field] ?? null;
    const b = before ? (before[field] ?? null) : null;
    let change: Change = "same";
    if (!before) change = "same";
    else if (filled(a) && !filled(b)) change = "gained";
    else if (!filled(a) && filled(b)) change = "lost";
    else if (a !== b) change = "changed";
    out.push({ field, now: a, before: b, change });
  }
  // Differences first, and among them the losses: a field that used to be read
  // and no longer is, is the one worth seeing before anything else.
  const rank: Record<Change, number> = { lost: 0, changed: 1, gained: 2, same: 3 };
  return out.sort(
    (x, y) => rank[x.change] - rank[y.change] || x.field.localeCompare(y.field),
  );
}

/**
 * Every attempt at extracting this document, and what each one read.
 *
 * The Re-run button that adds an attempt lives in the page header, beside the
 * document it acts on.
 *
 * Keyed on jobs rather than results, so an attempt that failed before writing
 * anything still appears — those are the ones a reviewer most wants to see, and
 * a history built from results would omit exactly them.
 *
 * Rows open. A score moving from 0% to 40% says a re-run helped; it does not say
 * whether the licence number finally came through, which is the thing the
 * reviewer actually wanted to know.
 */
export function DocumentHistory({ id }: { id: string }) {
  const path = documentHistoryPath(id);
  const { data, error, loading, reload } = useApi<History>(path);
  const [open, setOpen] = useState<string | null>(null);

  if (loading) return <Loading what="history" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const change = data.latest_change;
  const attempts = data.attempts;

  return (
    <>
      <div className="verify-meta">
        <span>
          {data.attempt_count} attempt{data.attempt_count === 1 ? "" : "s"} on this
          document
        </span>
        {attempts[0]?.completed_at ? (
          <span>last run {fmtDateTime(attempts[0].completed_at)}</span>
        ) : null}
        <span>select a row to see what it read</span>
      </div>

      {change ? (
        <div className={`notice${change.improved === false ? " notice-warn" : ""}`}>
          {change.required_score === null ? (
            <>
              The last re-run cannot be compared on required fields — no earlier
              attempt produced a scoreable result.
            </>
          ) : change.required_score === 0 ? (
            <>
              The last re-run changed nothing on required fields: still{" "}
              {pct(attempts[0].required_score)}.
            </>
          ) : change.required_score > 0 ? (
            <>
              <strong>The last re-run improved this document</strong> — required
              fields up {change.required_score} points to{" "}
              {pct(attempts[0].required_score)}.
            </>
          ) : (
            <>
              <strong>The last re-run made this document worse</strong> — required
              fields down {Math.abs(change.required_score)} points to{" "}
              {pct(attempts[0].required_score)}.
            </>
          )}
          {/* Which attempt the comparison is against, whenever it is not simply
              the row below — otherwise the reader subtracts the wrong two rows
              and concludes the figure is wrong. */}
          {change.skipped_attempts > 0 && change.compared_with_at ? (
            <>
              {" "}
              Compared with the run of {fmtDateTime(change.compared_with_at)}; the{" "}
              {change.skipped_attempts} attempt
              {change.skipped_attempts === 1 ? "" : "s"} in between produced nothing
              scoreable.
            </>
          ) : null}
        </div>
      ) : null}

      {attempts.length === 0 ? (
        <Empty
          title="No attempts recorded"
          hint="This document has no extraction jobs on file."
        />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col" style={{ width: 28 }}></th>
                <th scope="col">When</th>
                <th scope="col">Outcome</th>
                <th scope="col" className="num">Agent</th>
                <th scope="col" className="num">Gate</th>
                <th scope="col" className="num">Required</th>
                <th scope="col">Read</th>
                <th scope="col">Source</th>
                <th scope="col" className="num">Took</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a, i) => {
                const rows = diff(a.fields ?? {}, attempts[i + 1]?.fields ?? null);
                const isOpen = open === a.job_id;
                return (
                  <AttemptRows
                    key={a.job_id}
                    a={a}
                    latest={i === 0}
                    rows={rows}
                    isOpen={isOpen}
                    onToggle={() => setOpen(isOpen ? null : a.job_id)}
                    hasPrevious={Boolean(attempts[i + 1])}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function AttemptRows({
  a,
  latest,
  rows,
  isOpen,
  onToggle,
  hasPrevious,
}: {
  a: DocumentAttempt;
  latest: boolean;
  rows: FieldDiff[];
  isOpen: boolean;
  onToggle: () => void;
  hasPrevious: boolean;
}) {
  const failed = a.status === "failed";
  const filledCount = rows.filter(
    (r) => r.now !== null && r.now.trim() !== "",
  ).length;
  const gained = rows.filter((r) => r.change === "gained").length;
  const lost = rows.filter((r) => r.change === "lost").length;
  const changed = rows.filter((r) => r.change === "changed").length;

  return (
    <>
      <tr
        className={`row-link${latest ? " row-dirty" : ""}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-label={`${isOpen ? "Hide" : "Show"} what the run of ${fmtDateTime(
          a.created_at,
        )} read`}
      >
        <td className="disclosure">{isOpen ? "▾" : "▸"}</td>
        <td className="nowrap">
          {fmtDateTime(a.created_at)}
          <div className="muted" style={{ fontSize: 11 }}>
            {a.reprocess
              ? `re-run${a.requested_by ? ` by ${a.requested_by}` : ""}`
              : "first pass"}
          </div>
        </td>
        <td className="nowrap">
          <span className={`pill ${failed ? "pill-critical" : "pill-good"}`}>
            {humanise(a.status ?? "unknown")}
          </span>
          {a.reason_code ? (
            <div
              className="muted"
              style={{ fontSize: 11 }}
              title={a.error_message ?? undefined}
            >
              {a.reason_code}
            </div>
          ) : null}
        </td>
        <td className="num">
          {a.agent_confidence === null ? EM_DASH : `${a.agent_confidence}%`}
        </td>
        <td className="num muted">
          {a.gate_score === null ? EM_DASH : `${a.gate_score}%`}
        </td>
        <td className="num">
          {a.required_score === null ? (
            <span className="muted">{EM_DASH}</span>
          ) : (
            <span
              className={
                a.required_score === 100
                  ? "req-ok"
                  : a.required_score >= 60
                    ? "req-warn"
                    : "req-bad"
              }
            >
              {a.required_score}%
            </span>
          )}
          {a.required_total ? (
            <div className="muted" style={{ fontSize: 11 }}>
              {a.required_met}/{a.required_total}
            </div>
          ) : null}
        </td>
        {/* The count that makes a row worth opening: how much this attempt read,
            and how that differs from the one before it. */}
        <td className="nowrap" style={{ fontSize: 12.5 }}>
          {filledCount} field{filledCount === 1 ? "" : "s"}
          {hasPrevious && (gained || lost || changed) ? (
            <div style={{ fontSize: 11 }}>
              {gained ? <span className="req-ok">+{gained}</span> : null}
              {gained && (lost || changed) ? " " : null}
              {lost ? <span className="req-bad">−{lost}</span> : null}
              {lost && changed ? " " : null}
              {changed ? <span className="req-warn">~{changed}</span> : null}
              <span className="muted"> vs previous</span>
            </div>
          ) : hasPrevious ? (
            <div className="muted" style={{ fontSize: 11 }}>
              no change
            </div>
          ) : null}
        </td>
        <td style={{ fontSize: 12.5 }}>
          <span className="muted">
            {a.extraction_source
              ? a.extraction_source === "AI_AGENT_SERVICE_PARSER"
                ? "agent"
                : a.extraction_source === "REAL_PDF_OCR_TEXT_PARSER"
                  ? "regex fallback"
                  : "no file read"
              : EM_DASH}
          </span>
          {a.model_used ? (
            <div className="mono muted" style={{ fontSize: 11 }}>
              {a.model_used}
            </div>
          ) : null}
        </td>
        <td className="num muted">{fmtMs(a.execution_time_ms)}</td>
      </tr>

      {isOpen ? (
        <tr className="detail-row">
          <td colSpan={9}>
            {rows.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                This attempt produced no fields at all.
              </p>
            ) : (
              <div className="table-wrap">
                <table className="inner">
                  <thead>
                    <tr>
                      <th scope="col">Field</th>
                      <th scope="col">This attempt read</th>
                      <th scope="col">
                        {hasPrevious ? "The attempt before read" : ""}
                      </th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.field}>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {r.field}
                        </td>
                        <td className="value-cell">
                          {r.now === null ? (
                            <span className="muted">not in this extraction</span>
                          ) : r.now.trim() === "" ? (
                            <span className="muted">empty</span>
                          ) : (
                            r.now
                          )}
                        </td>
                        <td className="value-cell muted">
                          {!hasPrevious
                            ? ""
                            : r.before === null
                              ? "not in that extraction"
                              : r.before.trim() === ""
                                ? "empty"
                                : r.before}
                        </td>
                        <td className="nowrap">
                          {r.change === "gained" ? (
                            <span className="pill pill-good">gained</span>
                          ) : r.change === "lost" ? (
                            <span className="pill pill-critical">lost</span>
                          ) : r.change === "changed" ? (
                            <span className="pill pill-warning">changed</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
